import { animated, useTransition } from 'react-spring';
import React, { useMemo } from 'react';
import Avatar from '@atoms/Avatar';
import lodash from 'lodash';
import useFonts from '@lib/hooks/useFonts';

class TextMeasurer {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.measurementCache = new Map();
    this.isInitialized = false;
  }

  initialize() {
    if (typeof document !== 'undefined' && !this.isInitialized) {
      this.canvas = document.createElement('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.isInitialized = true;
    }
  }

  measureText(text, fontSize, fontFamily, width, lineHeight = 1.3) {
    if (typeof document === 'undefined') {
      return this.estimateTextHeight(text, fontSize, width, lineHeight);
    }

    this.initialize();

    if (!this.ctx) {
      return this.estimateTextHeight(text, fontSize, width, lineHeight);
    }

    const cacheKey = `${text}-${fontSize}-${fontFamily}-${width}`;

    if (this.measurementCache.has(cacheKey)) {
      return this.measurementCache.get(cacheKey);
    }

    this.ctx.font = `${fontSize} ${fontFamily}`;

    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine + ' ' + word;
      const metrics = this.ctx.measureText(testLine);

      if (metrics.width > width - 48) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);

    const height = lines.length * parseInt(fontSize) * lineHeight;
    this.measurementCache.set(cacheKey, height);

    return height;
  }

  estimateTextHeight(text, fontSize, width, lineHeight = 1.3) {
    const avgCharWidth = parseInt(fontSize) * 0.6;
    const availableWidth = width - 48;
    const charsPerLine = Math.floor(availableWidth / avgCharWidth);
    const estimatedLines = Math.ceil(text.length / charsPerLine);

    return Math.max(estimatedLines * parseInt(fontSize) * lineHeight, 100);
  }

  clearCache() {
    this.measurementCache.clear();
  }
}

const textMeasurer = new TextMeasurer();

export const getFontColor = (backgroundColor = '#000000') => {
  if (backgroundColor.charAt(0) === '#') {
    backgroundColor = backgroundColor.slice(1);
  }

  const red = parseInt(backgroundColor.substr(0, 2), 16);
  const green = parseInt(backgroundColor.substr(2, 2), 16);
  const blue = parseInt(backgroundColor.substr(4, 2), 16);

  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;

  if (brightness > 125) {
    return '#000000';
  }

  return '#FFFFFF';
};

export const getRandomColor = (currentRosFeature) => {
  const colors = currentRosFeature?.colors || currentRosFeature?.color;
  const isTetrisInpiredColors = !colors?.multipleColors;

  let TETRIS_COLORS = ['#B99AC7', '#81B1FA', '#B1D878', '#FF5497', '#FFE955', '#F26A60'];

  const bgColor1 = colors?.talkingTileBgColor1 || '#B99AC7';
  const bgColor2 = colors?.talkingTileBgColor2 || '#81B1FA';
  const bgColor3 = colors?.talkingTileBgColor3 || '#B1D878';
  if (isTetrisInpiredColors) {
    TETRIS_COLORS = [bgColor1, bgColor2, bgColor3];
  }
  return lodash.shuffle(TETRIS_COLORS)[0];
};

const Tile = React.memo(({ tile, entryProps, currentRosFeature, onPress }) => {
  if (!currentRosFeature || !tile) return null;

  const showUsername = currentRosFeature?.setting?.showUserName;
  const isVerticalLayout = currentRosFeature?.setting?.layoutMode === 'square';
  const isSandbox = currentRosFeature?.setting?.isSandbox;
  const fontFamily = currentRosFeature?.setting?.FontStyle || 'inter';
  const { getFontStyle } = useFonts();
  const cssFontFamily = useMemo(() => getFontStyle(fontFamily).fontFamily, [fontFamily, getFontStyle]);

  const setting = currentRosFeature?.setting;

  const { truncatedText, truncatedUsername } = useMemo(() => {
    const textLimit = isVerticalLayout ? 150 : isSandbox ? 100 : 300;
    const maxUsernameLength = 30;

    return {
      truncatedText: tile.words.length > textLimit ?
        tile.words.slice(0, textLimit) + '...' :
        tile.words,
      truncatedUsername: tile.username.length > maxUsernameLength ?
        tile.username.slice(0, maxUsernameLength) + '...' :
        tile.username
    };
  }, [tile.words, tile.username, isVerticalLayout, isSandbox]);

  const containerStyle = useMemo(() => {
    const TILE_GAP = 20;
    const LEFT_OFFSET = 10;
    const LEFT = tile.column * (tile.width + TILE_GAP) + LEFT_OFFSET;

    return {
      ...entryProps,
      position: 'absolute',
      left: `${LEFT}px`,
      width: `${tile.width}px`,
      height: `${tile.height}px`,
      padding: '20px 24px 20px 24px',
      borderRadius: '16px',
      boxShadow: tile.isFadingOut
        ? '0px 10px 0px rgba(255, 255, 255, 0.2)'
        : `0px 10px 0px ${darkenColor(tile.backgroundColor, 20)}`,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      // `top` is updated every frame by TileManager; animating it again causes jitter.
      transition: tile.isNew ? 'none' : 'box-shadow 0.1s ease-in-out',
      overflow: 'hidden',
      fontSize: isSandbox ? '16px' : tile.fontSize,
      willChange: 'top',
    };
  }, [entryProps, tile.column, tile.width, tile.height, tile.backgroundColor,
    tile.isFadingOut, tile.isNew, tile.fontSize, isSandbox]);

  const textStyle = useMemo(() => ({
    color: tile.fontColor || '#212121',
    margin: 0,
    lineHeight: '1.35',
    fontSize: isSandbox ? '20px' : (tile.fontSize || '24px'),
    wordWrap: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    fontFamily: cssFontFamily,
    textAlign: 'start',
    width: '100%',
  }), [tile.fontColor, tile.fontSize, cssFontFamily, isSandbox]);

  const usernameStyle = useMemo(() => ({
    fontSize: tile.userNameFontSize,
    color: tile.fontColor || '#212121',
    fontFamily: cssFontFamily
  }), [tile.userNameFontSize, tile.fontColor, cssFontFamily]);

  return <animated.div onClick={onPress} style={containerStyle}>
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'start',
        overflow: 'hidden',
        paddingTop: '8px',
        paddingBottom: '8px',

      }}
    >
      <p style={textStyle}>
        {truncatedText}
      </p>
    </div>
    {tile.username && showUsername && (
      <div className='flex items-center gap-2'>
        <Avatar isProfile staticColor='bg-grey-500' name={truncatedUsername} textClass='text-[14px]' />
        <p
          className={'text-grey-900 text-sm'}
          style={usernameStyle}
        >
          {truncatedUsername}
        </p>
      </div>
    )}
  </animated.div>;
});

class TileManager {
  constructor(container, currentRosFeature, chromaColor, textColor = '#111111') {
    this.container = container;
    this.tiles = [];
    this.columns = Array.from({ length: 3 }, () => []);
    this.onTilesUpdated = () => { };
    this.currentRosFeature = currentRosFeature;
    this.currentRosFeatureId = currentRosFeature?.id;
    this.chromaColor = chromaColor;
    this.textColor = textColor;
    this.commentQueue = [];
    this.lastTileColor = '';

    this.TILE_GAP = 30;
    this.FIXED_COLS = 3;
    this.INITIAL_OFFSET = 0;
    this.BOTTOM_PADDING = 20;
    this.BOTTOM_GAP = 1;
    this.MIN_TILE_HEIGHT = 144;
    this.fontScale = 1;

    if (container && typeof window !== 'undefined') {
      this.containerHeight = container.clientHeight || container.getBoundingClientRect().height;
      this.containerWidth = container.clientWidth || container.getBoundingClientRect().width;
    } else {
      this.containerHeight = 800;
      this.containerWidth = 1200;
    }

    this.maxTileHeight = this.containerHeight * 0.5;
    this.tileWidth = this.calculateTileWidth();
    this.numCols = this.FIXED_COLS;
    this.lastTileId = 0;
    this.DROP_SPEED = this.calculateDropSpeed();

    this.animationFrame = null;
    this.measureTextHeight = null;
    this.isRemovingTiles = false;
    this.visibilityChangeSet = false;
    this.hasMovingTiles = false;
  }

  getCurrentRosFeatureId() {
    return this.currentRosFeatureId;
  }

  setCurrentRosFeatureId(currentRosFeatureId) {
    this.currentRosFeatureId = currentRosFeatureId;
  }

  setTtId(ttId) {
    this.ttId = ttId;
  }

  calculateDropSpeed() {
    const BASE_SPEED = this.currentRosFeature?.setting?.isSandbox ? 0.8 : 1.2;

    return BASE_SPEED;
  }

  calculateTileWidth() {
    const isVerticalLayout = this.currentRosFeature?.setting?.layoutMode === 'square';
    const isSandbox = this.currentRosFeature?.setting?.isSandbox;
    const isPreview = this.currentRosFeature?.setting?.isPreview;
    let SIZE = 1920;

    if (isSandbox || isVerticalLayout) {
      SIZE = 1080;
    }

    if (this.currentRosFeature?.setting?.isOnboarding) {
      SIZE = 1520;
    }

    const width = isPreview ? SIZE * 1.1 : SIZE * (isVerticalLayout ? 0.75 : 0.85);
    return width / 3;
  }

  setOnTileRemoved(callback) {
    this.onTileRemoved = callback;
  }

  setContainer(container) {
    this.container = container;
    if (container && typeof window !== 'undefined') {
      this.containerWidth = container.clientWidth || container.getBoundingClientRect().width;
      this.containerHeight = container.clientHeight || container.getBoundingClientRect().height;
      this.tileWidth = this.calculateTileWidth();
    }
  }

  setContainerHeight(containerHeight) {
    this.containerHeight = Math.max(containerHeight || 0, 0);
    if (this.container && typeof window !== 'undefined') {
      this.containerWidth = this.container.clientWidth || this.container.getBoundingClientRect().width;
      this.maxTileHeight = this.containerHeight * 0.5;
      this.tileWidth = this.calculateTileWidth();
      this.DROP_SPEED = this.calculateDropSpeed();
    }
  }

  getNumCols() {
    return this.numCols;
  }

  setCurrentRosFeature(feature) {
    this.currentRosFeature = feature;
    this.chromaColor = feature?.color?.talkingTilesBgColor;
    this.updateExistingTiles();
    this.numCols = this.FIXED_COLS;
  }

  updateExistingTiles() {
    this.textColor = !this.currentRosFeature?.color?.multipleColors ?
      this.currentRosFeature?.color?.talkingTilesTextColor :
      '#111';

    this.tiles.forEach(tile => {
      tile.backgroundColor = getRandomColor(this.currentRosFeature);
      tile.fontColor = this.textColor;
    });

    this.onTilesUpdated([...this.tiles]);
  }

  createTile(comment, index, props = {}) {
    const words = comment.words || comment;
    const username = comment.username || '';
    const { height: tempTileHeight, fontSize, userNameFontSize } = this.createTempTile(words, username);
    const backgroundColor = getRandomColor(this.currentRosFeature);

    const tile = {
      id: this.lastTileId,
      comment_id: comment?.comment_id || '',
      words,
      username,
      backgroundColor,
      fontColor: this.textColor,
      column: index % this.FIXED_COLS,
      top: -tempTileHeight * 10,
      targetTop: -tempTileHeight * 10,
      width: this.tileWidth,
      height: tempTileHeight,
      visible: true,
      settled: false,
      isNew: true,
      fontSize,
      userNameFontSize,
      isBottom: false,
      ...props
    };

    this.lastTileId++;
    return tile;
  }

  dropTile({ comments, numCols = 3 }) {
    if (this.tiles.length > 10) {
      this.commentQueue.push(...comments);
      return;
    }

    const newTiles = comments.map((comment, index) =>
      this.createTile(comment, index, { isNew: true })
    );

    this.preCalculatePositions(newTiles);
    this.tiles = [...newTiles, ...this.tiles];
    this.reorganizeTiles();
    this.startAnimation();
  }

  preCalculatePositions(newTiles) {
    if (this.columns.length === 0) {
      this.columns = Array.from({ length: this.FIXED_COLS }, () => []);
    }

    const columnHeights = this.columns.map(column =>
      column.reduce((totalHeight, tile) => totalHeight + tile.height + this.TILE_GAP, 0)
    );

    const OFFSCREEN_BUFFER = 10;

    const allColumnsEmpty = this.columns.every(column => column.length === 0);

    const batchColumnTileCounts = this.columns.map(column => column.length);

    newTiles.forEach((tile, index) => {
      let selectedColumn;

      if (allColumnsEmpty) {
        selectedColumn = index % this.FIXED_COLS;
      } else {
        let minTiles = Math.min(...batchColumnTileCounts);
        const availableColumns = [];

        for (let i = 0; i < this.FIXED_COLS; i++) {
          if (batchColumnTileCounts[i] === minTiles) {
            availableColumns.push(i);
          }
        }

        selectedColumn = availableColumns[Math.floor(Math.random() * availableColumns.length)];
      }

      tile.column = selectedColumn;
      const currentColumn = this.columns[selectedColumn];

      batchColumnTileCounts[selectedColumn]++;

      let topmostTileTop = -Infinity;
      if (currentColumn.length > 0) {
        const sortedColumn = [...currentColumn].sort((a, b) => a.top - b.top);
        topmostTileTop = sortedColumn[0].top;
      }

      const respetiveBottomTileHeight = (currentColumn[0]?.height || tile.height) * 1.35;
      const topOffset = (OFFSCREEN_BUFFER + tile.height) + (index * respetiveBottomTileHeight / 3);

      if (currentColumn.length > 0 && topmostTileTop > -Infinity) {
        tile.top = Math.min(-(topOffset), topmostTileTop - tile.height - this.TILE_GAP * 5);
      } else {
        tile.top = -(topOffset);
      }

      columnHeights[selectedColumn] += tile.height + this.TILE_GAP;
      this.columns[selectedColumn].push(tile);
    });
  }

  reorganizeTiles(tiles = null) {
    const tilesToReorganize = tiles || this.tiles;
    this.numCols = 3;

    for (let i = 0; i < this.numCols; i++) {
      if (this.columns[i]) {
        this.columns[i].length = 0;
      } else {
        this.columns[i] = [];
      }
    }

    tilesToReorganize.forEach(tile => {
      const safeColumn = tile?.column >= this.numCols ?
        tile.column - 1 :
        tile?.column || 0;

      this.columns[safeColumn].push(tile);
    });

    this.columns.forEach((column, columnIndex) => {
      if (column.length === 0) return;

      let needsSort = false;
      for (let i = 1; i < column.length; i++) {
        if (column[i].top < column[i - 1].top) {
          needsSort = true;
          break;
        }
      }

      if (needsSort) {
        column.sort((a, b) => a.top - b.top);
      }

      if (column.length > 0) {
        for (let i = 0; i < column.length - 1; i++) {
          column[i].isBottom = false;
        }
        column[column.length - 1].isBottom = true;
      }

      let accumulatedHeight = 0;
      for (let i = column.length - 1; i >= 0; i--) {
        const tile = column[i];
        tile.targetTop = this.containerHeight - (this.BOTTOM_PADDING + this.BOTTOM_GAP + accumulatedHeight + tile.height);
        accumulatedHeight += tile.height + this.TILE_GAP;
      }
    });

    this.onTilesUpdated([...tilesToReorganize]);
  }

  startAnimation() {
    if (this.animationFrame) {
      return;
    }

    const animate = () => {
      this.animateTiles();

      if (this.hasMovingTiles || this.commentQueue.length > 0 || this.isRemovingTiles) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.animationFrame = null;
      }
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  animateTiles() {
    if (this.commentQueue.length > 0 && this.tiles.length <= 10) {
      const comments = this.commentQueue.splice(0, 3);
      const newTiles = comments.map((comment, index) =>
        this.createTile(comment, index, { isNew: true })
      );

      this.preCalculatePositions(newTiles);
      this.tiles = [...newTiles, ...this.tiles];
      this.reorganizeTiles();
    }

    const containerHeight = this.containerHeight || (this.currentRosFeature?.setting?.isPreview ? 720 : this.currentRosFeature?.setting?.isSandbox ? 470 : 820);
    const containerHeightThreshold = containerHeight * 0.7;
    this.hasMovingTiles = false;

    this.columns.forEach(column => {
      let settledHeight = 0;
      if (column.length === 0) return;

      column.sort((a, b) => a.targetTop - b.targetTop);

      for (let i = column.length - 1; i >= 0; i--) {
        const tile = column[i];
        const destinationTop = Number.isFinite(tile.targetTop) ? tile.targetTop : tile.top;
        const delta = destinationTop - tile.top;
        const absDelta = Math.abs(delta);

        if (absDelta <= 0.5) {
          tile.top = destinationTop;
          tile.settled = true;
          settledHeight += tile.height + this.TILE_GAP;
        } else {
          const step = Math.min(this.DROP_SPEED, absDelta);
          tile.top += Math.sign(delta) * step;
          tile.settled = false;
          this.hasMovingTiles = true;
        }
      }

      if (settledHeight > containerHeightThreshold && !this.isRemovingTiles) {
        this.removeBottomTile();
      }
    });

    if (!this.hasMovingTiles) {
      this.tiles.forEach(tile => {
        tile.isNew = false;
      });
    }

    this.onTilesUpdated([...this.tiles]);
  }

  getRemovedCommentIds() {
    if (typeof window === 'undefined') {
      return [];
    }

    const saveStateKey = `talking-tiles-state_${this.ttId || this.currentRosFeature.id}`;
    try {
      const saveStateData = localStorage.getItem(saveStateKey);
      const saveStateObject = saveStateData ?
        JSON.parse(saveStateData) :
        { comments: [], removedCommentIds: [] };

      return saveStateObject.removedCommentIds;
    } catch (error) {
      console.warn('Failed to get removed comment IDs:', error);
      return [];
    }
  }

  async removeBottomTile() {
    if (this.isRemovingTiles) {
      return;
    }

    const allColumnsHaveSettledBottom = this.columns.every(column =>
      column.length > 0 && column.some(tile => tile.isBottom && tile.settled)
    );

    const removedCommentIds = [];

    if (!allColumnsHaveSettledBottom) {
      return;
    }

    this.isRemovingTiles = true;
    try {
      const tobeRemovedTiles = [];

      for (const column of this.columns) {
        if (column && column.length > 0) {
          const bottomTileIndex = column.findIndex(tile => tile.isBottom && tile.settled);

          if (bottomTileIndex !== -1) {
            const removedTile = column[bottomTileIndex];
            removedTile.isFadingOut = true;
            removedCommentIds.push(removedTile.comment_id);
            tobeRemovedTiles.push({
              tile: removedTile,
              columnIndex: this.columns.indexOf(column),
            });
          }
        }
      }

      if (tobeRemovedTiles.length < 3) {
        return;
      }

      this.onTilesUpdated([...this.tiles]);
      await new Promise(resolve => setTimeout(resolve, 500));

      for (const { tile, columnIndex } of tobeRemovedTiles) {
        const column = this.columns[columnIndex];
        if (!column) continue;

        this.tiles = this.tiles.filter(t => t.id !== tile.id);
        const idx = column.findIndex(t => t.id === tile.id);
        if (idx !== -1) {
          column.splice(idx, 1);
        }
      }

      this.reorganizeTiles();
      this.columns.forEach(column => {
        column.forEach(tile => {
          tile.isNew = false;
          tile.settled = false;
        });
      });

      setTimeout(() => {
        if (typeof window === 'undefined') return;

        const saveStateKey = `talking-tiles-state_${this.ttId || this.currentRosFeature.id}`;
        try {
          const saveStateData = localStorage.getItem(saveStateKey);
          const saveStateObject = saveStateData ?
            JSON.parse(saveStateData) :
            { comments: [], removedCommentIds: [] };

          saveStateObject.removedCommentIds = [
            ...new Set([...saveStateObject?.removedCommentIds || [], ...removedCommentIds])
          ];

          localStorage.setItem(saveStateKey, JSON.stringify(saveStateObject));
        } catch (error) {
          console.warn('Failed to save removed comment IDs:', error);
        }
      }, 0);

      if (typeof this.onTileRemoved === 'function') {
        this.onTileRemoved();
      }
      this.startAnimation();
    } finally {
      setTimeout(() => {
        this.isRemovingTiles = false;
      }, 100);
    }
  }

  setOnTilesUpdated(callback) {
    this.onTilesUpdated = callback;
  }

  createTempTile(word, username) {
    const isSmallerLayout = this.currentRosFeature?.setting?.layoutMode === 'square' ||
      this.currentRosFeature?.setting?.isSandbox;
    const maxCharacters = isSmallerLayout ? 200 : 300;
    const truncatedText = word.length > maxCharacters
      ? word.slice(0, maxCharacters) + '...'
      : word;

    const baseFontSize = isSmallerLayout ? 24 : 28;
    const baseUserNameFontSize = isSmallerLayout ? 14 : 16;
    const scaledFontPx = Math.max(10, Math.round(baseFontSize * this.fontScale));
    const fontSize = `${scaledFontPx}px`;
    const userNameFontSize = `${baseUserNameFontSize}px`;
    const fontFamily = this.currentRosFeature?.setting?.FontStyle || 'inter';
    const { getFontStyle } = useFonts();
    const mappedFontFamily = getFontStyle(fontFamily).fontFamily;
    const lineHeight = 1.35;

    let height = textMeasurer.measureText(
      truncatedText,
      fontSize,
      mappedFontFamily,
      this.tileWidth,
      lineHeight
    );

    const verticalPadding = 40;
    const contentInnerPadding = 16;
    const descentBuffer = Math.ceil(scaledFontPx * 0.25);

    const minContentLines = 1;
    const minContentHeight = Math.round(scaledFontPx * lineHeight * minContentLines);

    let extraForUsername = 0;
    if (username && this.currentRosFeature?.setting?.showUserName) {
      const AVATAR_BASE_SIZE_PX = 32;
      const ROW_GAP_PX = 8;
      const usernameRowHeight = Math.max(AVATAR_BASE_SIZE_PX, Math.round(baseUserNameFontSize * 1.35));
      extraForUsername = usernameRowHeight + ROW_GAP_PX;
    }

    const measuredTotal = height + extraForUsername + verticalPadding + contentInnerPadding + descentBuffer;
    const baseMinTile = Math.round(this.MIN_TILE_HEIGHT * this.fontScale);
    const minHeight = Math.max(
      minContentHeight + extraForUsername + verticalPadding + contentInnerPadding + descentBuffer,
      baseMinTile + verticalPadding
    );

    const finalHeight = Math.max(measuredTotal, minHeight);
    return { height: finalHeight, fontSize, userNameFontSize };
  }

  clearTiles() {
    this.tiles = [];
    this.columns = [];
    this.cancelAnimation();
    this.commentQueue = [];
    this.onTilesUpdated([]);
  }

  cancelAnimation() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  resizeContainer(container) {
    const scaleFactor = 1.25;
    this.container = container || this.container;

    if (container && typeof window !== 'undefined') {
      const actualHeight = container.getBoundingClientRect().height;
      this.containerHeight = actualHeight / scaleFactor;
      this.containerWidth = container.getBoundingClientRect().width;
      this.maxTileHeight = this.containerHeight * 0.5;
      this.tileWidth = this.calculateTileWidth();
      this.DROP_SPEED = this.calculateDropSpeed();

      this.tiles.forEach(tile => {
        tile.width = this.tileWidth;
        tile.top = (tile.top / tile.height) * (this.containerHeight / scaleFactor);
        tile.targetTop = (tile.targetTop / tile.height) * (this.containerHeight / scaleFactor);
      });

      this.startAnimation();
    }
  }

  setMeasureTextHeight(callback) {
    this.measureTextHeight = callback;
  }

  setFontScale(scale) {
    if (!scale || scale <= 0) return;
    this.fontScale = scale;

    this.tiles.forEach(tile => {
      const { height, fontSize, userNameFontSize } = this.createTempTile(tile.words, tile.username);
      tile.height = height;
      tile.fontSize = fontSize;
      tile.userNameFontSize = userNameFontSize;
      tile.width = this.tileWidth;
      tile.settled = false;
      tile.isNew = false;
    });

    this.reorganizeTiles();
    this.startAnimation();
  }

  destroy() {
    this.clearTiles();
    this.cancelAnimation();

    textMeasurer.clearCache();

    if (this.visibilityChangeSet && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }

    this.onTilesUpdated = () => { };
    this.onTileRemoved = () => { };
    this.container = null;
    this.currentRosFeature = null;
  }

  getTilesState() {
    const visibleTiles = this.tiles.filter(tile => tile.top + tile.height > 0);
    return visibleTiles.map(tile => ({
      comment_id: tile.comment_id || '',
      ...tile,
      isNew: false,
      isSaved: true,
    }));
  }

  getColumnIndexToDropIn() {
    const emptyColumns = [];
    for (let i = 0; i < this.FIXED_COLS; i++) {
      if (this.columns[i].length === 0) {
        emptyColumns.push(i);
      }
    }

    if (emptyColumns.length > 0) {
      return emptyColumns[Math.floor(Math.random() * emptyColumns.length)];
    }

    const nonEmptyColumns = this.columns.filter(column => column.length > 0);
    const columnWithLeastTilesHeight = nonEmptyColumns.reduce((minColumn, column) =>
      column.reduce((minHeight, tile) =>
        tile.height + tile.top < minHeight ? tile.height + tile.top : minHeight,
      Infinity
      ) < minColumn.reduce((minHeight, tile) =>
        tile.height + tile.top < minHeight ? tile.height + tile.top : minHeight,
      Infinity
      ) ? column : minColumn
    );
    return this.columns.indexOf(columnWithLeastTilesHeight);
  }

  restoreTilesState(savedState) {
    this.tiles = [];
    this.columns = Array.from({ length: this.numCols }, () => []);
    this.tiles = savedState.map((savedTile, index) => {
      const tile = this.createTempTile(savedTile.words, savedTile.username);
      return {
        id: savedTile.id || this.lastTileId++,
        comment_id: savedTile.comment_id || '',
        words: savedTile.words,
        username: savedTile.username || '',
        backgroundColor: savedTile.backgroundColor || getRandomColor(this.currentRosFeature),
        fontColor: savedTile.fontColor || this.textColor,
        column: savedTile.column !== undefined ? savedTile.column : index % this.FIXED_COLS,
        top: savedTile.top,
        targetTop: savedTile.targetTop,
        width: this.tileWidth,
        height: tile.height,
        visible: true,
        settled: savedTile.settled || false,
        isNew: false,
        isSaved: true,
        fontSize: savedTile.fontSize,
        userNameFontSize: savedTile.userNameFontSize,
        isBottom: savedTile.isBottom || false
      };
    });

    const maxId = Math.max(...this.tiles.map(tile => tile.id), 0);
    this.lastTileId = maxId + 1;

    this.tiles.forEach(tile => {
      if (this.columns[tile.column]) {
        this.columns[tile.column].push(tile);
      } else {
        const safeColumn = tile.column % this.numCols;
        tile.column = safeColumn;
        this.columns[safeColumn].push(tile);
      }
    });

    this.columns.forEach((column, columnIndex) => {
      column.sort((a, b) => a.top - b.top);

      if (column.length > 0) {
        column[column.length - 1].isBottom = true;
      }
    });

    this.onTilesUpdated([...this.tiles]);

    this.startAnimation();
  }
}

const darkenColor = (color, percent) => {
  if (!color) return color;
  // Handle gradient colors - return a semi-transparent dark shadow
  if (typeof color === 'string' && color.startsWith('linear-gradient')) {
    return 'rgba(0, 0, 0, 0.2)';
  }
  // Handle non-hex colors
  if (typeof color !== 'string' || !color.startsWith('#')) {
    return 'rgba(0, 0, 0, 0.2)';
  }
  const num = parseInt(color.slice(1), 16);
  const amt = Math.round(2.55 * percent);
  const r = (num >> 16) - amt;
  const g = ((num >> 8) & 0x00ff) - amt;
  const b = (num & 0x0000ff) - amt;
  return `rgb(${Math.max(r, 0)}, ${Math.max(g, 0)}, ${Math.max(b, 0)})`;
};

export const AnimatedTile = ({ tile, currentRosFeature, onPress }) => {
  const transitions = useTransition([tile], {
    keys: tile.id,
    from: {
      top: -tile.height,
      opacity: 0,
      background: tile.backgroundColor
    },
    enter: {
      top: tile.top,
      opacity: tile.isFadingOut ? 0.5 : 1,
      background: tile.backgroundColor
    },
    update: {
      top: tile.top,
      opacity: tile.isFadingOut ? 0.5 : 1,
      background: tile.backgroundColor
    },
    leave: {
      opacity: 0,
      transform: 'scale(0.8)'
    },
    // Keep `top` immediate so movement is controlled only by TileManager.
    immediate: key => key === 'top',
    config: {
      tension: 120,
      friction: 20
    }
  });

  return transitions((style, item) =>
    item ? (
      <Tile
        onPress={onPress}
        tile={item}
        entryProps={style}
        currentRosFeature={currentRosFeature}
      />
    ) : null
  );
};

export default TileManager;
